import React, { useCallback, useEffect, useRef, useState } from 'react';

import Grid from '@mui/material/Grid2';

import {
  Character,
  CharacterFeature,
  Proficiency,
  CharacterProficiency,
  Tool,
  Skill,
  CharacterSpell,
  CharacterItem,
  SummonOption2,
  PseudoItem,
  SKILLS,
} from "@dndModels";
import { ModelBase } from "@models";

import DataUtilities from "@dndUtilities/data_utilities";

import DisplayStringWithReplacements from "@dndComponents/display/DisplayStringWithReplacements";
import CharacterListOptionsInput from "@dndComponents/character/misc/CharacterListOptionsInput";

interface Props {
  character: Character;
  char_feature: CharacterFeature;
  onChange: () => void;
}

const data_util = DataUtilities.getInstance();

export default function CharacterExpertisesInput({ 
  character,
  char_feature,
  onChange
}: Props) {
  const selected_options = char_feature.feature_options as CharacterProficiency[];
  const [fetched, setFetched] = useState(false);
  const [prof_ids, setProfIds] = useState<string[]>(selected_options.map(o => o.proficiency_id).filter(o => o !== ""));
  const [select_changed, setSelectChanged] = useState(false);
  const [reloading, setReloading] = useState(false);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  const recalculated = useCallback(() => {
    setReloading(true);
  }, [setReloading]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      character.subscription_holder.subscribe("Recalculated", mounted, recalculated);
    }
  }, [character, fetched, setFetched, mounted, recalculated]);
  
  useEffect(() => {
    if (select_changed) {
      setSelectChanged(false);
      // debugger;
      const options = char_feature.feature_options as CharacterProficiency[];
      const remove_us = options.map(o => o.proficiency_id).filter(o => o !== "" && !prof_ids.includes(o));
      for (const prof_id of remove_us) {
        const opt = options.find(o => o.proficiency_id === prof_id); // options.find(o => o.proficiency_id === "");
        if (opt) {
          opt.proficiency_id = "";
        }
      }
      for (const prof_id of prof_ids) {
        let opt = options.find(o => o.proficiency_id === prof_id); // options.find(o => o.proficiency_id === "");
        if (!opt) {
          opt = options.find(o => o.proficiency_id === "");
          if (opt) {
            opt.proficiency_id = prof_id;
          }
        }
      }
      character.changed();
      character.subscription_holder.recalculate();
    }
  }, [character, char_feature, prof_ids, select_changed, setSelectChanged]);

  const ff = char_feature.floating_feature as Proficiency;
  
  const all_prof_ids = character.proficiencies.map(o => o.proficiency_id);
  const all_expertise_ids = character.expertises.map(o => o.proficiency_id);
  if (ff.proficiency_types.length > 1 || 
    ff.proficiency_types[0] === "Skill" || 
    ff.proficiency_types[0] === "Tool") {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Expertise
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <DisplayStringWithReplacements display_string={ ff.description } cfs={character} />
        </Grid>
        { ff.proficiency_types.map(prof_type => {
          let use_proficiencies: (Skill[] | Tool[]) = []; 
          // debugger;
          if (prof_type === "Skill") {
            use_proficiencies = SKILLS.filter(o => all_prof_ids.includes(o._id) && (!all_expertise_ids.includes(o._id) || prof_ids.includes(o._id)));
          } else {
            use_proficiencies = data_util.tools.filter(o => all_prof_ids.includes(o._id) && (!all_expertise_ids.includes(o._id) || prof_ids.includes(o._id)));
          } 
          return (
            <Grid
              key={prof_type}
              size={{
                xs: 12
              }}>
              <CharacterListOptionsInput
                data_type={prof_type}
                character={character}
                choose_max={char_feature.feature_options.length} 
                use_objects={use_proficiencies}
                selected={prof_ids} 
                force_selectable
                onChange={(changed: string[]) => {
                  setProfIds(changed);
                  setSelectChanged(true);
                }} 
                getDisabledReasons={(obj: ModelBase | CharacterSpell | CharacterItem | SummonOption2 | PseudoItem) => {
                  const options = char_feature.feature_options as CharacterProficiency[];
                  if (options.filter(o => o.proficiency_id === obj._id).length === 0) {
                    if (obj) {
                      if (character.expertises.filter(o => o.proficiency_id === obj._id).length > 0) {
                        return "Already Known";
                      }
                    }
                  }
                  return "";
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }
  return <></>;
}
